import { useEffect, useRef } from 'react';

const FOCUSABLE = 'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])';

interface OverlayEntry {
  id: string;
  close: () => void;
}

const openOverlays: OverlayEntry[] = [];

let scrollLockCount = 0;
function lockBodyScroll() {
  scrollLockCount += 1;
  if (scrollLockCount === 1) document.body.style.overflow = 'hidden';
}
function unlockBodyScroll() {
  scrollLockCount = Math.max(0, scrollLockCount - 1);
  if (scrollLockCount === 0) document.body.style.overflow = '';
}

let _seq = 0;

export interface UseOverlayOptions {
  trapFocus?: boolean;
  restoreFocus?: boolean;
  lockScroll?: boolean;
  closeOnEscape?: boolean;
  closeOnOutsideClick?: boolean;
  ignoreRefs?: React.RefObject<Element | null>[];
  exclusive?: boolean;
  initialFocus?: 'first' | 'container';
  onReposition?: (() => void) | null;
  containerRef?: React.RefObject<HTMLElement | null>;
}

export function useOverlay(
  open: boolean,
  onClose: () => void,
  options: UseOverlayOptions = {},
): React.RefObject<HTMLElement | null> {
  const {
    trapFocus = true,
    restoreFocus = true,
    lockScroll = false,
    closeOnEscape = true,
    closeOnOutsideClick = true,
    ignoreRefs = [],
    exclusive = true,
    initialFocus = 'first',
    onReposition = null,
    containerRef: externalContainerRef = null,
  } = options;

  const ownContainerRef = useRef<HTMLElement | null>(null);
  const containerRef = externalContainerRef || ownContainerRef;
  const openerRef = useRef<Element | null>(null);
  const idRef = useRef<string | null>(null);
  if (idRef.current == null) idRef.current = `overlay_${(_seq += 1)}`;

  const prevOpenRef = useRef(false);
  if (open && !prevOpenRef.current && !openerRef.current) {
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (active && active !== document.body && !(containerRef.current && containerRef.current.contains(active))) {
      openerRef.current = active;
    }
  }
  prevOpenRef.current = open;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const ignoreRefsRef = useRef(ignoreRefs);
  ignoreRefsRef.current = ignoreRefs;
  const onRepositionRef = useRef(onReposition);
  onRepositionRef.current = onReposition;

  useEffect(() => {
    if (!open) return undefined;
    if (exclusive) {
      const others = openOverlays.filter((e) => e.id !== idRef.current);
      openOverlays.length = 0;
      openOverlays.push({ id: idRef.current!, close: () => onCloseRef.current() });
      others.forEach((e) => { try { e.close(); } catch { /* no-op */ } });
    } else {
      openOverlays.push({ id: idRef.current!, close: () => onCloseRef.current() });
    }
    return () => {
      const i = openOverlays.findIndex((e) => e.id === idRef.current);
      if (i >= 0) openOverlays.splice(i, 1);
    };
  }, [open, exclusive]);

  useEffect(() => {
    if (open) {
      if (!openerRef.current) {
        const active = document.activeElement;
        const inside = containerRef.current && active && containerRef.current.contains(active);
        if (active && active !== document.body && !inside) {
          openerRef.current = active;
        } else {
          const fallback = ignoreRefs[0];
          openerRef.current = (fallback && fallback.current) || null;
        }
      }
      if (lockScroll) lockBodyScroll();
      const raf = requestAnimationFrame(() => {
        const el = containerRef.current;
        if (!el || el.contains(document.activeElement)) return;
        if (initialFocus === 'container') {
          if (typeof el.focus === 'function') { try { el.focus(); } catch { /* no-op */ } }
          return;
        }
        const f = el.querySelectorAll(FOCUSABLE);
        const target = (f[0] || el) as HTMLElement;
        if (target && typeof target.focus === 'function') { try { target.focus(); } catch { /* no-op */ } }
      });
      return () => {
        cancelAnimationFrame(raf);
        if (lockScroll) unlockBodyScroll();
      };
    }
    const opener = openerRef.current;
    openerRef.current = null;
    if (restoreFocus && opener && typeof (opener as HTMLElement).focus === 'function') {
      try { (opener as HTMLElement).focus(); } catch { /* no-op */ }
    }
    return undefined;
  }, [open, lockScroll, restoreFocus, initialFocus]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (!trapFocus || e.key !== 'Tab') return;
      const el = containerRef.current;
      if (!el) return;
      const f = el.querySelectorAll(FOCUSABLE);
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0] as HTMLElement;
      const last = f[f.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (!el.contains(active) || active === el) { e.preventDefault(); (e.shiftKey ? last : first).focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeOnEscape, trapFocus]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!closeOnOutsideClick) return;
      const el = containerRef.current;
      if (el && el.contains(e.target as Node)) return;
      const ignored = ignoreRefsRef.current || [];
      for (let i = 0; i < ignored.length; i += 1) {
        const r = ignored[i];
        if (r && r.current && r.current.contains(e.target as Node)) return;
      }
      onCloseRef.current();
    };
    if (closeOnOutsideClick) document.addEventListener('mousedown', onDown);
    let mainEl: HTMLElement | null = null;
    const onResize = () => { if (onRepositionRef.current) onRepositionRef.current(); };
    if (onReposition) {
      window.addEventListener('resize', onResize);
      mainEl = document.getElementById('main');
      if (mainEl) mainEl.addEventListener('scroll', onResize);
    }
    return () => {
      if (closeOnOutsideClick) document.removeEventListener('mousedown', onDown);
      if (onReposition) {
        window.removeEventListener('resize', onResize);
        if (mainEl) mainEl.removeEventListener('scroll', onResize);
      }
    };
  }, [open, closeOnOutsideClick, !!onReposition]);

  return containerRef;
}

export default useOverlay;
